import {
	Component,
	computed,
	DestroyRef,
	inject,
	Injector,
	Input,
	OnInit,
	Pipe,
	PipeTransform,
	Signal,
	signal,
	WritableSignal,
	ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	BillingService,
	BillingUserRole,
	ReallocateTarget,
	TenantUnitReference,
	TenantUnitReferenceService,
	TenantUnitUserReferenceService,
	UserRole,
	UserRoleService,
} from '@eadsysteme/billingclient-v1';
import { SessionService } from '../../../../../services/session.service';
import { ActivatedRoute } from '@angular/router';

import { BreadCrumbService } from '../../../../../../@vex/components/breadcrumbs/BreadCrumbService';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MatList, MatListItem } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { DateUtil } from '../../../../../util/DateUtil';
import { fadeInUp400ms } from '../../../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../../../@vex/animations/stagger.animation';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { DelayedSearchDirective } from '../../../../../directives/delayed-search.directive';
import { Icons } from '../../../../../util/Icons';
import { Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ReallocateInstanceDialogComponent } from './billing-instance/reallocate-instance-dialog/reallocate-instance-dialog.component';
import { IconHelper } from '../../../../devices/IconHelper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import dayjs, { Dayjs } from 'dayjs';
import { finalize } from 'rxjs/operators';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { ReallocateUserDialogComponent } from './billing-instance/reallocate-user-dialog/reallocate-user-dialog.component';
import { ConfirmationDialogComponent } from '../../../../confirmation-dialog/confirmation-dialog.component';
import { MatTooltip } from '@angular/material/tooltip';
import { AddUserRoleDialogComponent } from './billing-instance/add-user-role-dialog/add-user-role-dialog.component';
import { EditUserRoleDialogComponent } from './billing-instance/edit-user-role-dialog/edit-user-role-dialog.component';
import { TenantUnitReferenceComponent } from './billing-instance/tenant-unit-reference.component';

@Pipe({
	standalone: true,
	name: 'filter',
})
export class FilterPipe implements PipeTransform {
	private readonly injector = inject(Injector);

	transform(value: Array<any>, callback: any): any {
		return value.filter(callback);
	}
}

@Component({
	selector: 'ara-tenant-unit-references-master',
	imports: [
		TranslocoDirective,
		MatIcon,
		MatIconButton,
		MatMenu,
		MatMenuItem,
		MatMenuTrigger,
		ReactiveFormsModule,
		MatListItem,
		MatList,
		DelayedSearchDirective,
		MatDatepickerModule,
		MatNativeDateModule,
		MatFormFieldModule,
		MatInput,
		MatButton,
		MatProgressSpinner,
		MatRadioGroup,
		MatRadioButton,
		MatTooltip,
		TenantUnitReferenceComponent,
	],
	animations: [fadeInUp400ms, stagger40ms],
	templateUrl: './tenant-unit-references-master.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './tenant-unit-references-master.component.scss',
})
export class TenantUnitReferencesMasterComponent implements OnInit {
	private readonly activeRoute = inject(ActivatedRoute);
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialog = inject(MatDialog);
	private readonly formBuilder = inject(FormBuilder);
	private readonly snackbar = inject(SnackbarService);
	private readonly translocoService = inject(TranslocoService);
	protected readonly tenantUnitReferenceService = inject(TenantUnitReferenceService);
	protected readonly tenantUnitUserReferenceService = inject(TenantUnitUserReferenceService);
	protected readonly billingService = inject(BillingService);
	protected readonly userRoleService = inject(UserRoleService);
	protected readonly sessionService = inject(SessionService);
	protected readonly breadCrumbService = inject(BreadCrumbService);
	protected readonly iconHelper = inject(IconHelper);
	protected readonly dateUtil = inject(DateUtil);
	protected readonly icons = inject(Icons);

	protected finishedLoading: WritableSignal<boolean> = signal(true);

	@Input() billingId: string;
	tenantUnitReferences: WritableSignal<TenantUnitReference[]> = signal([]);
	availableUserRoles: WritableSignal<UserRole[]> = signal([]);
	filteredTenantUnitReferences: Signal<TenantUnitReference[]> = signal([]);
	selectedTenantUnitReference: WritableSignal<TenantUnitReference> = signal(undefined);
	anyInstanceUnsynced: Signal<boolean> = signal(false);
	searchFilterSubject: Subject<string> = new Subject<string>();
	filterFormGroup: FormGroup;

	validFromFilter: WritableSignal<Dayjs> = signal(undefined);
	validToFilter: WritableSignal<Dayjs> = signal(undefined);
	userRoleTypeFilter: WritableSignal<UserRoleTypeFilterEnum> = signal(UserRoleTypeExtensionEnum.BILLING_UNIT);
	searchTerm: WritableSignal<string> = signal('');

	showAddUserRoleButton: Signal<boolean> = signal(false);

	constructor() {
		if (!this.billingId) {
			this.billingId = this.activeRoute.snapshot.paramMap?.get('billingId') ?? '';
		}
		this.createForm();

		this.filteredTenantUnitReferences = computed(() => {
			return this.filterTenantUnitReferences();
		});

		this.anyInstanceUnsynced = computed(() => {
			return this.tenantUnitReferences()?.filter((bI) => this.anyInstanceItemUnsynced(bI)).length > 0;
		});

		this.showAddUserRoleButton = computed(() => {
			const availableUserRoleTypes: Set<BillingUserRole> = new Set(this.availableUserRoles()?.map((ur) => ur.userRoleType));
			return availableUserRoleTypes.size < Object.keys(BillingUserRole).length;
		});
	}

	ngOnInit(): void {
		this.loadTenantUnitReferences();
		this.loadAvailableUserRoles();
		this.searchFilterSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (searchTerm: string) => {
				this.searchTerm.set(searchTerm?.trim() ?? '');
			},
		});
	}

	private loadAvailableUserRoles() {
		this.filterFormGroup.controls.userRole.disable();
		this.userRoleService
			.readAllUserRole(this.sessionService.effectiveArea, this.billingId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (roles) => {
					this.availableUserRoles.set(roles);
					if (roles.length > 0) {
						this.filterFormGroup.controls.userRole.enable();
					}
				},
				error() {
					this.snackbar.error(this.translocoService.translate('errors.loadingBillingUserRoles'));
				},
			});
	}

	private loadTenantUnitReferences() {
		this.finishedLoading.set(false);
		this.tenantUnitReferenceService
			.readAllTenantUnitReference(this.sessionService.effectiveArea, this.billingId)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.finishedLoading.set(true))
			)
			.subscribe({
				next: (response) => {
					response.sort((a, b) => {
						return a.number === b.number ? a.subNumber - b.subNumber : a.number - b.number;
					});
					for (const tenantUnitReference of response) {
						// sort users by validTo date descending, i.e. latest first
						tenantUnitReference.tenantUnitUserReferences?.sort((a, b) => {
							return dayjs(a.validTo).isAfter(dayjs(b.validTo), 'day') ? -1 : 1;
						});
					}
					this.tenantUnitReferences.set(response);
					if (this.selectedTenantUnitReference()?.exportId) {
						this.selectedTenantUnitReference.set(
							this.tenantUnitReferences().find((bI) => bI.exportId === this.selectedTenantUnitReference().exportId)
						);
					} else {
						this.selectedTenantUnitReference.set(undefined);
					}
				},
				error: () => {
					this.snackbar.error(this.translocoService.translate('errors.loadingTenantUnitReferences'));
					this.tenantUnitReferences.set([]);
				},
			});
	}

	private filterTenantUnitReferences() {
		let tenantUnitReferences = structuredClone(this.tenantUnitReferences()); // deep copy
		if (!tenantUnitReferences?.length) {
			return [];
		}

		const searchTerm = this.searchTerm()?.toLowerCase();
		tenantUnitReferences = this.filterTenantUnitReferencesBySearchTerm(tenantUnitReferences, searchTerm);

		// only filter by date if at least one date filter is set
		if (this.validFromFilter() || this.validToFilter()) {
			const filterValidFrom = this.validFromFilter()?.isValid() ? this.validFromFilter() : dayjs('1970-01-01');
			const filterValidTo = this.validToFilter()?.isValid() ? this.validToFilter() : dayjs('9999-12-31');

			tenantUnitReferences = this.filterTenantUnitReferencesByDate(tenantUnitReferences, filterValidFrom, filterValidTo);
		}

		tenantUnitReferences = this.filterTenantUnitReferencesByUserRoleType(tenantUnitReferences, this.userRoleTypeFilter());

		const tenantUnitReferencesByUser: TenantUnitReference[] = [];
		// for each user we need a single item
		for (const tenantUnitReference of tenantUnitReferences) {
			if (!tenantUnitReference.tenantUnitUserReferences?.length) {
				tenantUnitReferencesByUser.push(tenantUnitReference);
				return;
			}
			for (const tenantUnitReferenceUser of tenantUnitReference.tenantUnitUserReferences) {
				const tenantUnitReferenceWithSingleUser = structuredClone(tenantUnitReference);
				tenantUnitReferenceWithSingleUser.tenantUnitUserReferences = [tenantUnitReferenceUser];
				tenantUnitReferencesByUser.push(tenantUnitReferenceWithSingleUser);
			}
		}

		// re-order by number and subNumber
		tenantUnitReferencesByUser.sort((a, b) => {
			return a.number === b.number ? a.subNumber - b.subNumber : a.number - b.number;
		});

		// list unsync instances first
		tenantUnitReferencesByUser.sort((a) => {
			const sort = this.userRoleTypeFilter() === UserRoleTypeExtensionEnum.BILLING_UNIT ? a.inSync : !this.anyUserOfInstanceUnsynced(a);
			return sort ? 1 : -1;
		});

		return tenantUnitReferencesByUser;
	}

	private filterTenantUnitReferencesByUserRoleType(tenantUnitReferences: TenantUnitReference[], userRoleTypeFilter: UserRoleTypeFilterEnum) {
		// if filtering by role, filter out any instance without users of the role
		if (userRoleTypeFilter === UserRoleTypeExtensionEnum.BILLING_UNIT) {
			// otherwise, take the latest user of each instance to display their name next to the instance number
			return tenantUnitReferences.map((tenantUnitReference) => {
				const tenants = tenantUnitReference.tenantUnitUserReferences?.filter((tUUR) => tUUR.userRole?.userRoleType === BillingUserRole.TENANT);
				const residents = tenantUnitReference.tenantUnitUserReferences?.filter((tUUR) => tUUR.userRole?.userRoleType === BillingUserRole.RESIDENT);
				const owners = tenantUnitReference.tenantUnitUserReferences?.filter((tUUR) => tUUR.userRole?.userRoleType === BillingUserRole.OWNER);
				// tenant first, otherwise resident, otherwise owner, ensure array will be empty if no users of the role exist
				tenantUnitReference.tenantUnitUserReferences = [tenants?.at(0) ?? residents?.at(0) ?? owners?.at(0)].filter((tUUR) => tUUR !== undefined);
				return tenantUnitReference;
			});
		} else {
			return tenantUnitReferences
				.filter((bI) => bI.tenantUnitUserReferences?.length > 0)
				.map((tenantUnitReference) => {
					tenantUnitReference.tenantUnitUserReferences = tenantUnitReference.tenantUnitUserReferences?.filter(
						(tUUR) => userRoleTypeFilter === tUUR?.userRole?.userRoleType
					);
					return tenantUnitReference;
				});
		}
	}

	private filterTenantUnitReferencesByDate(tenantUnitReferences: TenantUnitReference[], filterValidFrom: Dayjs, filterValidTo: Dayjs) {
		// filter billing instances by valid dates first
		const tenantUnitReferencesCopy = tenantUnitReferences.filter((bI) => {
			const instanceValidFrom = dayjs(bI.validFrom);
			const instanceValidTo = dayjs(bI.validTo);
			return instanceValidTo.isSameOrAfter(filterValidFrom, 'day') && instanceValidFrom.isSameOrBefore(filterValidTo, 'day');
		});

		// filter users as well, but only if a user role is selected
		if (this.userRoleTypeFilter() !== UserRoleTypeExtensionEnum.BILLING_UNIT) {
			for (const tenantUnitReference of tenantUnitReferencesCopy) {
				tenantUnitReference.tenantUnitUserReferences = tenantUnitReference.tenantUnitUserReferences?.filter((tUUR) => {
					const instanceUserValidTo = dayjs(tUUR.validTo);
					const instanceUserValidFrom = dayjs(tUUR.validFrom);
					return instanceUserValidTo.isSameOrAfter(filterValidFrom, 'day') && instanceUserValidFrom.isSameOrBefore(filterValidTo, 'day');
				});
			}
		}
		return tenantUnitReferencesCopy;
	}

	private filterTenantUnitReferencesBySearchTerm(tenantUnitReferences: TenantUnitReference[], searchTerm: string) {
		if (!searchTerm?.length || !tenantUnitReferences?.length) {
			return tenantUnitReferences;
		}
		return tenantUnitReferences.filter((tenantUnitReference) => {
			const tenantUnitReferenceNumber = tenantUnitReference.number + '/' + tenantUnitReference.subNumber;
			if (tenantUnitReferenceNumber.includes(searchTerm)) {
				return true;
			}
			const filteredInstanceUsers = tenantUnitReference.tenantUnitUserReferences?.filter((tUUR) => tUUR.name?.toLowerCase().includes(searchTerm));
			return !!filteredInstanceUsers.length;
		});
	}

	private createForm() {
		this.filterFormGroup = this.formBuilder.group(
			{
				validFrom: [{ value: null, disabled: false }],
				validTo: [{ value: null, disabled: false }],
				userRole: [{ value: this.userRoleTypeFilter(), disabled: true }],
			},
			{
				validators: [],
			}
		);

		this.filterFormGroup.markAllAsTouched();
		this.subscribeForm();
	}

	private subscribeForm() {
		this.filterFormGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (values) => {
				this.validFromFilter.set(values.validFrom);
				this.validToFilter.set(values.validTo);
				this.userRoleTypeFilter.set(values.userRole);
			},
		});
	}

	protected syncManually() {
		this.finishedLoading.set(false);
		this.billingService
			.syncTenantUnitReferencesBilling(this.sessionService.effectiveArea, this.billingId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.snackbar.success(this.translocoService.translate('billing.synchronizedTenantUnitReferences'));
					this.loadTenantUnitReferences();
				},
				error: () => {
					this.snackbar.error(this.translocoService.translate('errors.synchronizingTenantUnitReferences'));
					this.finishedLoading.set(true);
				},
			});
	}

	protected reAllocateUser(currentInstance: TenantUnitReference) {
		const reallocateTenantUnitReference = {
			billingId: this.billingId,
			instanceToMigrate: currentInstance,
		};
		const matDialogRef = this.dialog.open(ReallocateUserDialogComponent, {
			disableClose: true,
			width: '600px',
			data: reallocateTenantUnitReference,
		});

		matDialogRef.afterClosed().subscribe((reallocateRequest: { sourceExportId: string; target: ReallocateTarget }) => {
			if (reallocateRequest) {
				this.tenantUnitReferenceService
					.reallocateTenantUnitUserReference(
						this.sessionService.effectiveArea,
						this.billingId,
						currentInstance.exportId,
						reallocateRequest.sourceExportId,
						reallocateRequest.target
					)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: (value) => {
							if (value) {
								this.snackbar.success(this.translocoService.translate('billing.userReallocated'));
							}
							this.loadTenantUnitReferences();
						},
						error: () => {
							this.snackbar.error(this.translocoService.translate('errors.reallocateBillingUser'));
						},
					});
			}
		});
	}

	protected reAllocateInstance(currentInstance: TenantUnitReference) {
		const reallocateTenantUnitReference = {
			billingId: this.billingId,
			instanceToMigrate: currentInstance,
		};
		const matDialogRef = this.dialog.open(ReallocateInstanceDialogComponent, {
			disableClose: true,
			width: '600px',
			data: reallocateTenantUnitReference,
		});

		matDialogRef.afterClosed().subscribe((reallocateRequest: { sourceExportId: string; target: ReallocateTarget }) => {
			if (reallocateRequest) {
				this.tenantUnitReferenceService
					.reallocateTenantUnitReference(
						this.sessionService.effectiveArea,
						this.billingId,
						reallocateRequest.sourceExportId,
						reallocateRequest.target
					)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: (value) => {
							if (value) {
								this.snackbar.success(this.translocoService.translate('billing.instanceReallocated'));
							}
							this.loadTenantUnitReferences();
						},
						error: () => {
							this.snackbar.error(this.translocoService.translate('errors.reallocateTenantUnitReference'));
						},
					});
			}
		});
	}

	protected anyInstanceItemUnsynced(currentInstance: TenantUnitReference): boolean {
		return !currentInstance.inSync || this.anyUserOfInstanceUnsynced(currentInstance);
	}

	protected instanceUnsyncedByRoleFilter(currentInstance: TenantUnitReference): boolean {
		if (this.userRoleTypeFilter() === UserRoleTypeExtensionEnum.BILLING_UNIT) {
			return !currentInstance.inSync;
		}
		return this.anyUserOfInstanceUnsynced(currentInstance);
	}

	protected anyUserOfInstanceUnsynced(currentInstance: TenantUnitReference) {
		if (currentInstance.tenantUnitUserReferences.length > 0) {
			return currentInstance.tenantUnitUserReferences.some((value) => !value.inSync);
		}
		return false;
	}

	protected selectInstance(TenantUnitReference: TenantUnitReference) {
		const oldId = this.selectedTenantUnitReference()?.exportId;
		this.selectedTenantUnitReference.set(TenantUnitReference);
		if (oldId !== TenantUnitReference.exportId) {
			this.loadTenantUnitReferences();
		}
	}

	protected deleteUserRole(userRole: UserRole) {
		if (!userRole) {
			return;
		}
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('errors.warning');
		let userRoleString = this.translocoService.translate('enum.' + userRole.userRoleType);
		if (userRole.name?.trim()?.length) {
			userRoleString += ' (' + userRole.name + ')';
		}
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate('billing.confirmDeleteUserRole', { name: userRoleString });

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.userRoleService
					.deleteUserRole(this.sessionService.effectiveArea, this.billingId, userRole.exportId)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: (deletedUserRole: UserRole) => {
							if (!deletedUserRole) {
								this.snackbar.error(this.translocoService.translate('errors.deletingBillingUserRole'));
								return;
							}
							if (this.userRoleTypeFilter() === userRole.userRoleType) {
								this.filterFormGroup.controls.userRole.setValue(UserRoleTypeExtensionEnum.BILLING_UNIT);
								this.userRoleTypeFilter.set(UserRoleTypeExtensionEnum.BILLING_UNIT);
							}
							this.loadAvailableUserRoles();
							this.snackbar.success(this.translocoService.translate('billing.deleteUserRoleSuccess'));
						},
						error: () => {
							this.snackbar.error(this.translocoService.translate('errors.deletingBillingUserRole'));
						},
					});
			}
		});
	}

	protected editUserRole(userRole: UserRole) {
		if (!userRole) {
			return;
		}
		const dialogRef = this.dialog.open(EditUserRoleDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
			width: '600px',
			data: userRole,
		});

		dialogRef.afterClosed().subscribe((newUserRole: UserRole) => {
			if (newUserRole) {
				this.userRoleService
					.updateUserRole(this.sessionService.effectiveArea, this.billingId, userRole.exportId, newUserRole)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: (savedUserRole: UserRole) => {
							if (!savedUserRole) {
								this.snackbar.error(this.translocoService.translate('errors.editBillingUserRole'));
								return;
							}
							this.snackbar.success(this.translocoService.translate('billing.userRoleUpdated'));
							this.loadAvailableUserRoles();
						},
						error: () => {
							this.snackbar.error(this.translocoService.translate('errors.editBillingUserRole'));
						},
					});
			}
		});
	}

	protected addUserRole() {
		const availableUserRoleTypes: BillingUserRole[] = Object.values(BillingUserRole).filter(
			(ur) =>
				!this.availableUserRoles()
					?.map((aUR) => aUR.userRoleType)
					?.includes(ur)
		);

		const dialogData = {
			billingId: this.billingId,
			availableUserRoleTypes,
		};
		const dialogRef = this.dialog.open(AddUserRoleDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
			width: '600px',
			data: dialogData,
		});

		dialogRef.afterClosed().subscribe((newUserRole: UserRole) => {
			if (newUserRole) {
				this.userRoleService
					.createUserRole(this.sessionService.effectiveArea, this.billingId, newUserRole)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: (savedUserRole: UserRole) => {
							if (!savedUserRole) {
								this.snackbar.error(this.translocoService.translate('errors.addBillingUserRole'));
								return;
							}
							this.snackbar.success(this.translocoService.translate('billing.userRoleAdded'));
							this.loadAvailableUserRoles();
							this.loadTenantUnitReferences();
						},
						error: () => {
							this.snackbar.error(this.translocoService.translate('errors.addBillingUserRole'));
						},
					});
			}
		});
	}

	protected readonly UserRoleTypeExtensionEnum = UserRoleTypeExtensionEnum;
}

enum UserRoleTypeExtensionEnum {
	BILLING_UNIT = 'BILLING_UNIT',
}

type UserRoleTypeFilterEnum = BillingUserRole | UserRoleTypeExtensionEnum;
